import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GBELogo } from "@/components/layout/GBELogo";
import { TID } from "@/constants/testIds";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login, pending2FA, verify2FA, cancelPending2FA } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const dest = location.state?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await login(email, password);
      if (!r.requires_2fa) {
        if (r.must_change_password) {
          toast.warning("Please set a new password to continue.");
          nav("/security");
        } else {
          toast.success("Welcome back");
          nav(dest);
        }
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const on2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await verify2FA(otp);
      if (r?.must_change_password) {
        toast.warning("Please set a new password to continue.");
        nav("/security");
      } else {
        toast.success("Verified");
        nav(dest);
      }
    } catch (err) {
      toast.error(err.message);
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center p-6">
      <div className="w-full max-w-[440px]">
        <div className="mb-8 flex justify-center">
          <div className="rounded-lg px-4 py-3 bg-gbe">
            <GBELogo />
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-[0_1px_2px_rgba(15,23,42,0.04),0_20px_60px_-30px_rgba(15,23,42,0.2)] p-8">
          {!pending2FA ? (
            <>
              <div className="mb-6">
                <h1 className="font-display text-2xl font-semibold text-slate-900">Sign in</h1>
                <p className="text-sm text-slate-500 mt-1">
                  Access your consolidated company data across every GBE system and connected client platform.
                </p>
              </div>
              <form onSubmit={onLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-slate-700">Work email</Label>
                  <Input
                    id="email"
                    data-testid={TID.loginEmail}
                    type="email"
                    autoComplete="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-slate-700">Password</Label>
                  <Input
                    id="password"
                    data-testid={TID.loginPassword}
                    type="password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-1.5"
                  />
                  <div className="mt-2 text-right">
                    <Link data-testid="login-forgot-link" to="/forgot-password" className="text-xs text-gbe hover:text-gbe font-medium">
                      Forgot password?
                    </Link>
                  </div>
                </div>
                <Button
                  type="submit"
                  data-testid={TID.loginSubmit}
                  disabled={loading}
                  className="w-full bg-gbe hover:bg-gbe-hover text-white h-10"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
                </Button>
              </form>
              <div className="mt-6 text-center text-xs text-slate-500">
                Access is by invitation. Contact your administrator to be added.
              </div>
            </>
          ) : (
            <>
              <div className="mb-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-gbe" />
                </div>
                <div>
                  <h1 className="font-display text-xl font-semibold text-slate-900">Two-factor code</h1>
                  <p className="text-xs text-slate-500">Enter the 6-digit code from your Authenticator app.</p>
                </div>
              </div>
              <form onSubmit={on2FA} className="space-y-5">
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid={TID.twofaCode}>
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <InputOTPSlot key={i} index={i} />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <Button
                  type="submit"
                  data-testid={TID.twofaSubmit}
                  disabled={loading || otp.length < 6}
                  className="w-full bg-gbe hover:bg-gbe-hover text-white h-10"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & continue"}
                </Button>
                <button
                  type="button"
                  data-testid={TID.twofaCancel}
                  onClick={cancelPending2FA}
                  className="w-full text-xs text-slate-500 hover:text-slate-800"
                >
                  Cancel and use a different account
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          GBE Group internal system. Access is logged and monitored.
        </p>
      </div>
    </div>
  );
}
